import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Create styles that match your website exactly
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column', // Changed to column for landscape layout
    backgroundColor: '#FFFFFF', // White background
    padding: 24,
    fontFamily: 'Helvetica',
  },
  
  // Container for the four rectangles and text below
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  
  // Row container for squares with text below
  row: {
    flexDirection: 'column',
    flex: 1,
    gap: 8, // Reduced space between squares and their text
    marginBottom: 12, // Reduced space between rows
  },
  
  // Container for squares in each row
  squaresRow: {
    flexDirection: 'row',
    gap: 24, // Space between squares
    flex: 1, // Reduced height to fit on one page
  },
  
  // Container for text below squares in each row
  textRow: {
    flexDirection: 'row',
    gap: 24,
    flex: 0.15, // Further reduced height for text
  },
  
  // Dark grey rectangle (upper left)
  greyCard: {
    backgroundColor: '#282828', // Dark charcoal grey
    borderRadius: 16,
    padding: 24,
    flex: 1,
    // Remove border, use background gradient for shadow effect
    background: 'linear-gradient(135deg, #282828 0%, #1a1a1a 100%)',
  },
  
  // Light grey rectangle (upper right)
  greyCard2: {
    backgroundColor: '#f5f5f5', // Very light grey
    borderRadius: 16,
    padding: 24,
    flex: 1,
    // Add subtle gradient for depth
    background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)',
  },
  
  // Light grey rectangle (lower left)
  greyCard3: {
    backgroundColor: '#f5f5f5', // Very light grey
    borderRadius: 16,
    padding: 24,
    flex: 1,
    // Add subtle gradient for depth
    background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)',
  },
  
  // Light grey rectangle (lower right)
  greyCard4: {
    backgroundColor: '#f5f5f5', // Very light grey
    borderRadius: 16,
    padding: 24,
    flex: 1,
    // Add subtle gradient for depth
    background: 'linear-gradient(135deg, #f5f5f5 0%, #eeeeee 100%)',
  },
  
  // Text styles for rectangles (centered)
  cardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 8,
    textAlign: 'center',
    fontFamily: 'Helvetica',
  },
  
  cardDescription: {
    fontSize: 12,
    color: '#333333',
    lineHeight: 1.4,
    textAlign: 'center',
    fontFamily: 'Helvetica',
  },
  
  // Text styles for text below rectangles (left aligned, smaller)
  textTitle: {
    fontSize: 12, // Reduced from 14
    fontWeight: 'bold',
    color: '#2c2c2c', // Dark grey instead of black
    marginBottom: 6,
    textAlign: 'left',
    fontFamily: 'Helvetica',
    paddingLeft: 8, // Slight right shift
  },
  
  textDescription: {
    fontSize: 10, // Reduced from 11
    color: '#2c2c2c', // Dark grey instead of #333333
    lineHeight: 1.3,
    textAlign: 'left',
    fontFamily: 'Helvetica',
    paddingLeft: 8, // Slight right shift
  },
  
  // Text column container
  textColumn: {
    flex: 1,
  },
  
  // Summary statistics styles for left rectangle
  summaryContainer: {
    padding: 32,
  },
  
  personName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF', // White text for dark background
    marginBottom: 20,
    textAlign: 'left',
    fontFamily: 'Helvetica',
  },
  
  infoSection: {
    marginBottom: 16,
  },
  
  infoLabel: {
    fontSize: 12,
    color: '#CCCCCC', // Light grey for dark background
    marginBottom: 4,
    fontFamily: 'Helvetica',
  },
  
  infoValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#FFFFFF', // White text for dark background
    fontFamily: 'Helvetica',
  },
  
  strategySection: {
    marginTop: 24,
    paddingTop: 16,
    borderTop: '1px solid #555555', // Darker border for dark background
  },
  
  strategyTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FFFFFF', // White text for dark background
    marginBottom: 12,
    fontFamily: 'Helvetica',
  },
  
  // Button styles for summary cards
  buttonContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    justifyContent: 'space-between',
    paddingTop: 0, // No top margin
    paddingBottom: 32, // More bottom margin to move buttons up
    paddingHorizontal: 8,
    alignItems: 'center', // Center content vertically
    justifyContent: 'center', // Center content horizontally
  },
  
  summaryButton: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8, // Less curved corners
    paddingHorizontal: 12,
    paddingVertical: 10,
    width: '47%', // Slightly smaller to fit better
    marginBottom: 10,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 45,
  },
  
  buttonText: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#6B46C1', // Purple for data values
    fontFamily: 'Helvetica',
    textAlign: 'center',
  },
  
  buttonLabel: {
    fontSize: 8,
    fontWeight: 'normal',
    color: '#666666', // Dark grey for labels
    fontFamily: 'Helvetica',
    textAlign: 'center',
    marginTop: 2,
  },
  
  // Graph styles for payment visualization (matching portal)
  graphContainer: {
    padding: 16,
    alignItems: 'center',
  },
  
  graphTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#000000',
    marginBottom: 12,
    textAlign: 'center',
    fontFamily: 'Helvetica',
  },
  
  graphArea: {
    width: '100%',
    height: 80,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    border: '1px solid #e5e7eb',
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  
  graphBar: {
    backgroundColor: '#3399FF', // Blue accent matching portal
    borderRadius: 2,
    width: '6%',
    marginHorizontal: 1,
    minHeight: 4,
  },
  
  graphText: {
    fontSize: 9,
    color: '#666666',
    textAlign: 'center',
    marginTop: 6,
    fontFamily: 'Helvetica',
  },
});

const NewBeautifulPDF = ({ loanData }) => {
  console.log('NewBeautifulPDF component rendering...');
  console.log('loanData received:', loanData);
  
  // Calculate payment progression for graph
  const generatePaymentData = () => {
    const basePayment = Number(loanData?.minPayment) || 100;
    const increaseRate = loanData?.increasePayments ? 0.05 : 0; // 5% annual increase
    const months = 12; // Show 12 months
    
    const data = Array.from({ length: months }, (_, i) => {
      const yearIncrease = Math.floor(i / 12) * increaseRate;
      const monthlyPayment = basePayment * (1 + yearIncrease);
      return Math.round(monthlyPayment);
    });
    
    console.log('Generated payment data:', data);
    return data;
  };
  
  const paymentData = generatePaymentData();
  const maxPayment = Math.max(...paymentData);
  
  console.log('Payment data for graph:', paymentData);
  console.log('Max payment:', maxPayment);
  
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.container}>
          {/* First row: squares with text below */}
          <View style={styles.row}>
            {/* Squares */}
            <View style={styles.squaresRow}>
              <View style={styles.greyCard}>
                <View style={styles.buttonContainer}>
                  {/* Total loan button */}
                  <View style={styles.summaryButton}>
                    <Text style={styles.buttonText}>
                      €{loanData?.totalDebt ? Number(loanData.totalDebt).toLocaleString('nl-NL') : '0'}
                    </Text>
                    <Text style={styles.buttonLabel}>
                      Totale lening
                    </Text>
                  </View>
                  
                  {/* Stelsel button */}
                  <View style={styles.summaryButton}>
                    <Text style={styles.buttonText}>
                      {loanData?.stelsel === 'old' ? '15 jaar' : '35 jaar'}
                    </Text>
                    <Text style={styles.buttonLabel}>
                      Aflosstelsel
                    </Text>
                  </View>
                  
                  {/* Minimum payment button */}
                  <View style={styles.summaryButton}>
                    <Text style={styles.buttonText}>
                      €{loanData?.minPayment ? Number(loanData.minPayment).toLocaleString('nl-NL') : '0'}
                    </Text>
                    <Text style={styles.buttonLabel}>
                      Minimale aflossing
                    </Text>
                  </View>
                  
                  {/* Monthly payment button */}
                  <View style={styles.summaryButton}>
                    <Text style={styles.buttonText}>
                      €{loanData?.minPayment ? Number(loanData.minPayment).toLocaleString('nl-NL') : '0'}
                    </Text>
                    <Text style={styles.buttonLabel}>
                      Maandelijkse aflossing
                    </Text>
                  </View>
                  
                  {/* Annual increase button */}
                  <View style={styles.summaryButton}>
                    <Text style={styles.buttonText}>
                      {loanData?.increasePayments ? 'Ja' : 'Nee'}
                    </Text>
                    <Text style={styles.buttonLabel}>
                      Jaarlijkse stijging
                    </Text>
                  </View>
                  
                  {/* Investment percentage button */}
                  <View style={styles.summaryButton}>
                    <Text style={styles.buttonText}>
                      {loanData?.investmentPercentage ? `${loanData.investmentPercentage}%` : '0%'}
                    </Text>
                    <Text style={styles.buttonLabel}>
                      % Investeren
                    </Text>
                  </View>
                </View>
              </View>
              <View style={styles.greyCard2}>
                {/* Empty light rectangle - no text */}
              </View>
            </View>
            {/* Text below squares */}
            <View style={styles.textRow}>
              <View style={styles.textColumn}>
                <Text style={styles.textTitle}>Jouw aflosresultaten in één oogopslag</Text>
                <Text style={styles.textDescription}>Bekijk direct wat jouw strategie betekent: totale kosten, rente, waarde van investeringen, risicoprofiel en verwachte looptijd.</Text>
              </View>
              <View style={styles.textColumn}>
                <Text style={styles.textTitle}>Vul je persoonlijke situatie in</Text>
                <Text style={styles.textDescription}>Voer je studieschuld, DUO-stelsel en maandelijkse aflossing in. Stel je strategie af met slimme opties zoals investeren of jaarlijkse stijging.</Text>
              </View>
            </View>
          </View>
          
          {/* Second row: squares with text below */}
          <View style={styles.row}>
            {/* Squares */}
            <View style={styles.squaresRow}>
              <View style={styles.greyCard3}>
                <View style={styles.graphContainer}>
                  <Text style={styles.graphTitle}>Maandelijkse Aflossing</Text>
                  
                  <View style={styles.graphArea}>
                    {paymentData.map((payment, index) => {
                      const barHeight = (payment / maxPayment) * 60; // Scale to graph height
                      return (
                        <View
                          key={index}
                          style={[
                            styles.graphBar,
                            { height: Math.max(barHeight, 4) } // Minimum height of 4px
                          ]}
                        />
                      );
                    })}
                  </View>
                  
                  <Text style={styles.graphText}>
                    {loanData?.increasePayments 
                      ? `Stijging van €${paymentData[0]} naar €${paymentData[paymentData.length - 1]} per maand`
                      : `Stabiel €${paymentData[0]} per maand`
                    }
                  </Text>
                </View>
              </View>
              <View style={styles.greyCard4}>
                {/* Empty light rectangle - no text */}
              </View>
            </View>
            {/* Text below squares */}
            <View style={styles.textRow}>
              <View style={styles.textColumn}>
                <Text style={styles.textTitle}>Inzicht in je lening over de tijd</Text>
                <Text style={styles.textDescription}>Volg hoe je studieschuld, rente en investeringen zich ontwikkelen. De grafiek laat direct zien wat jouw keuzes betekenen voor de toekomst.</Text>
              </View>
              <View style={styles.textColumn}>
                <Text style={styles.textTitle}>Speel met je strategie, zie het effect</Text>
                <Text style={styles.textDescription}>Pas het maandbedrag of investeringsniveau aan en zie meteen hoe dat invloed heeft op looptijd, kosten en rendement.</Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default NewBeautifulPDF;
